/**
 * Auth BroadcastChannel message schema + lock / skew constants. RESEARCH §Pattern 2.
 *
 * The discriminated union lets every subscriber exhaustively match via ts-pattern:
 *
 *   match(msg)
 *     .with({ type: "TOKEN_REFRESHED" }, (m) => ...)
 *     .with({ type: "LOGOUT" }, () => ...)
 *     .with({ type: "FAMILY_REVOKED" }, (m) => ...)
 *     .exhaustive();
 *
 * Adding a new variant forces a compile error at every match site.
 *
 * Constants:
 *   - AUTH_CHANNEL_NAME — the BroadcastChannel name; all tabs join the same name.
 *   - AUTH_LOCK_NAME    — Web Locks API key; single-flight refresh across tabs.
 *   - REFRESH_SKEW_SECONDS — schedule refresh this many seconds BEFORE `exp` so
 *     clock skew + network round-trip don't cause a 401 race.
 */

export type AuthBroadcastMessage =
  | {
      type: "TOKEN_REFRESHED";
      accessToken: string;
      exp: number;
      issuedAt: number;
      /**
       * Permission codes fetched alongside the token rotation. `undefined`
       * when the leader couldn't reach the perms endpoint (fail-closed); other
       * tabs treat undefined as "keep prior perms" rather than overwriting
       * with empty (avoids a transient gate-deny flash mid-rotation).
       *
       * Included in the BC message so follower tabs sync without a
       * redundant /effective-permissions fetch each. Plan 08.1-02.
       */
      effectivePermissions?: readonly string[];
      /** The refresh response's `mustChangePassword` (see AuthSnapshot). */
      mustChangePassword?: boolean;
    }
  | { type: "LOGOUT" }
  | { type: "FAMILY_REVOKED"; reason: "reuse" | "explicit"; correlationId: string }
  | {
      /**
       * Shared refresh-throttle cooldown. localStorage is the primary
       * transport; this BC mirror keeps the cooldown cross-tab when storage
       * is disabled (private mode), where the memory fallback would otherwise
       * be tab-local and every queued tab would burn its own 429 retry.
       * `untilMs: 0` lifts the cooldown (bucket recovered).
       */
      type: "REFRESH_COOLDOWN";
      untilMs: number;
    };

export const AUTH_CHANNEL_NAME = "auth" as const;
export const AUTH_LOCK_NAME = "auth-refresh" as const;
export const REFRESH_SKEW_SECONDS = 60 as const;

/**
 * Call inside a `ts-pattern` `.exhaustive()` default to assert all message
 * variants are handled. The `never` parameter type means adding a new variant
 * surfaces as a compile error at every consumer.
 */
export function assertUnreachable(x: never): never {
  throw new Error(`Unexpected auth broadcast message variant: ${JSON.stringify(x)}`);
}
